import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Users, 
  AlertTriangle,
  TrendingUp,
  Calendar,
  ChevronRight,
  Bell,
  Moon,
  Dumbbell,
  Apple,
  Settings,
  LogOut,
  UserPlus,
  Trash2,
  Utensils,
  ShoppingCart,
  Clock,
  Target,
  CheckCircle,
  CalendarDays,
  GraduationCap,
  Activity,
  ClipboardList,
  Trophy
} from 'lucide-react';
import SupporterNotifications from '../components/SupporterNotifications';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, isToday, isTomorrow, startOfWeek, endOfWeek } from 'date-fns';

const SupporterDashboard = () => {
  const { user, logout } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addingAthlete, setAddingAthlete] = useState(false);
  const [athleteEmail, setAthleteEmail] = useState('');
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [processingVerification, setProcessingVerification] = useState(null);

  useEffect(() => {
    fetchAthletes();
    fetchFamilySchedule();
    fetchPendingVerifications();
  }, []);

  const fetchPendingVerifications = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/workouts/matrix/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingVerifications(response.data.pending_verifications || []);
    } catch (error) {
      console.error('Failed to fetch pending verifications:', error);
      setPendingVerifications([]);
    }
  };

  const handleVerification = async (completionId, action) => {
    if (processingVerification) return; // Prevent double-clicks
    
    setProcessingVerification(completionId);
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/workouts/matrix/verify`, 
        { completion_id: completionId, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === 'approve' ? 'Workout verified!' : 'Workout rejected');
      // Remove from local state immediately for instant UI feedback
      setPendingVerifications(prev => prev.filter(v => v.completion_id !== completionId));
    } catch (error) {
      toast.error('Failed to process verification');
      // Refetch to restore correct state if there was an error
      fetchPendingVerifications();
    } finally {
      setProcessingVerification(null);
    }
  };

  const fetchAthletes = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/family/athletes`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAthletes(response.data.athletes || []);
      setAlerts(response.data.alerts || []);
    } catch (error) {
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilySchedule = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/family/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const events = response.data.events || [];
      
      // Filter for today's events
      const today = events.filter(e => {
        const eventDate = new Date(e.start_time);
        return isToday(eventDate);
      }).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      
      // Filter for upcoming events (next 7 days)
      const upcoming = events.filter(e => {
        const eventDate = new Date(e.start_time);
        const now = new Date();
        const weekEnd = endOfWeek(now);
        return eventDate >= now && eventDate <= weekEnd;
      }).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      
      setTodayTasks(today);
      setUpcomingEvents(upcoming);
    } catch (error) {
      setTodayTasks([]);
      setUpcomingEvents([]);
    }
  };

  const getAthleteById = (id) => athletes.find(a => a.user_id === id);

  const getEventTypeColor = (type) => {
    const colors = {
      practice: 'bg-aura-primary',
      game: 'bg-green-500',
      personal_training: 'bg-purple-500',
      conditioning: 'bg-orange-500',
      workout: 'bg-aura-secondary',
      school: 'bg-blue-500',
      family: 'bg-pink-500',
      recovery: 'bg-cyan-500',
      appointment: 'bg-amber-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const handleAddAthlete = async (e) => {
    e.preventDefault();
    if (!athleteEmail.trim()) {
      toast.error('Please enter athlete email');
      return;
    }
    
    setAddingAthlete(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/family/link`, 
        { athlete_email: athleteEmail },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      toast.success(response.data.message || 'Athlete linked successfully!');
      setAthleteEmail('');
      setShowAddDialog(false);
      fetchAthletes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add athlete');
    } finally {
      setAddingAthlete(false);
    }
  };

  const handleRemoveAthlete = async (athleteEmail, athleteName) => {
    if (!confirm(`Remove ${athleteName} from your family account?`)) return;
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/family/unlink`, 
        { athlete_email: athleteEmail },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      toast.success(`${athleteName} removed from family`);
      fetchAthletes();
    } catch (error) {
      toast.error('Failed to remove athlete');
    }
  };

  const getRecoveryColor = (score) => {
    if (!score) return 'text-aura-text-muted';
    if (score >= 80) return 'text-aura-success';
    if (score >= 60) return 'text-aura-secondary';
    if (score >= 40) return 'text-aura-warning';
    return 'text-aura-destructive';
  };

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-aura-secondary rounded-sm flex items-center justify-center">
              <span className="font-heading font-black text-white text-lg">A</span>
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">AURA</span>
            <span className="text-xs text-aura-text-muted uppercase tracking-wider hidden sm:block">Supporter</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Milestone Notifications */}
            <SupporterNotifications />

            {/* Alerts Bell */}
            <button className="relative p-2 text-aura-text-muted hover:text-white transition-colors">
              <Bell className="w-5 h-5" />
              {alerts.length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-aura-destructive rounded-full" />
              )}
            </button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-aura-secondary flex items-center justify-center">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <span className="font-heading font-bold text-white text-sm">
                        {user?.name?.charAt(0)?.toUpperCase() || 'S'}
                      </span>
                    )}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-aura-surface border-white/10">
                <div className="px-3 py-2">
                  <p className="font-medium text-white">{user?.name}</p>
                  <p className="text-sm text-aura-text-muted">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem asChild>
                  <Link to="/alerts/settings" className="flex items-center gap-2 text-white cursor-pointer">
                    <Bell className="w-4 h-4" />
                    Alert Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2 text-white cursor-pointer">
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-aura-destructive cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Welcome */}
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
            Family Development System
          </h1>
          <p className="text-aura-text-muted mt-1">
            Support your athletes' journey to greatness.
          </p>
        </div>

        {/* Alerts Section */}
        {alerts.length > 0 && (
          <Card className="bg-aura-destructive/10 border-aura-destructive/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-aura-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">Attention Needed</p>
                  <ul className="text-sm text-aura-text-muted mt-1 space-y-1">
                    {alerts.map((alert, index) => (
                      <li key={index}>{alert.message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Workout Verifications */}
        {pendingVerifications.length > 0 && (
          <Card className="bg-amber-500/10 border-amber-500/30" data-testid="pending-verifications">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-amber-400" />
                  Pending Approvals ({pendingVerifications.length})
                </CardTitle>
              </div>
              <p className="text-sm text-aura-text-muted">
                Your athletes completed these drills and need your confirmation
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingVerifications.map((verification) => (
                  <div 
                    key={verification.completion_id} 
                    className={`flex items-center justify-between p-4 bg-white/5 rounded-lg transition-opacity ${processingVerification === verification.completion_id ? 'opacity-50' : ''}`}
                    data-testid={`verification-${verification.completion_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <Dumbbell className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{verification.drill_name}</p>
                        <p className="text-sm text-aura-text-muted">
                          {verification.athlete_name} • {verification.completed_at ? format(parseISO(verification.completed_at), 'MMM d, h:mm a') : 'Just now'}
                        </p>
                        {verification.category && (
                          <Badge className="mt-1 bg-white/10 text-white border-0 text-xs">
                            {verification.category}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        onClick={() => handleVerification(verification.completion_id, 'reject')}
                        disabled={processingVerification === verification.completion_id}
                        data-testid={`reject-${verification.completion_id}`}
                      >
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleVerification(verification.completion_id, 'approve')}
                        disabled={processingVerification === verification.completion_id}
                        data-testid={`approve-${verification.completion_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {processingVerification === verification.completion_id ? 'Processing...' : 'Approve'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's Responsibilities - What athletes need to do */}
        {todayTasks.length > 0 && (
          <Card className="bg-gradient-to-r from-aura-primary/10 to-aura-secondary/10 border-aura-primary/30">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <Clock className="w-5 h-5 text-aura-primary" />
                  Today's Schedule ({todayTasks.length})
                </CardTitle>
                <Link to="/family/schedule" className="text-aura-secondary text-sm font-medium">
                  View All
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {todayTasks.slice(0, 4).map((event) => {
                  const athlete = getAthleteById(event.athlete_id);
                  return (
                    <div key={event.event_id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <div className={`w-2 h-10 rounded-full ${getEventTypeColor(event.event_type)}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white truncate">{event.title}</p>
                          {event.requires_parent && (
                            <Badge className="bg-aura-secondary/20 text-aura-secondary border-0 text-xs">
                              You Needed
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-aura-text-muted">
                          {format(parseISO(event.start_time), 'h:mm a')}
                          {athlete ? ` • ${athlete.name}` : ' • Family'}
                          {event.location ? ` • ${event.location}` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions for Parents */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Link to="/coach/assignments">
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/30 hover:border-purple-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <ClipboardList className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white uppercase tracking-tight text-sm">Assign</p>
                    <p className="text-xs text-aura-text-muted">Workouts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/family/workouts">
            <Card className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/30 hover:border-orange-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                    <Dumbbell className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white uppercase tracking-tight text-sm">Workouts</p>
                    <p className="text-xs text-aura-text-muted">View activities</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/family/schedule">
            <Card className="bg-gradient-to-br from-aura-primary/10 to-aura-primary/5 border-aura-primary/30 hover:border-aura-primary/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-12 h-12 bg-aura-primary/20 rounded-xl flex items-center justify-center">
                    <CalendarDays className="w-6 h-6 text-aura-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white uppercase tracking-tight text-sm">Calendar</p>
                    <p className="text-xs text-aura-text-muted">Manage schedules</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/family/meal-plan">
            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/30 hover:border-green-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <Utensils className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white uppercase tracking-tight text-sm">Food Prefs</p>
                    <p className="text-xs text-aura-text-muted">Allergies & dislikes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/meal-plan">
            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/30 hover:border-amber-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                    <Apple className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white uppercase tracking-tight text-sm">Meal Plan</p>
                    <p className="text-xs text-aura-text-muted">14-day rotation</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/grocery-list">
            <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border-cyan-500/30 hover:border-cyan-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white uppercase tracking-tight text-sm">Grocery</p>
                    <p className="text-xs text-aura-text-muted">Shopping helper</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/supporter/leaderboard">
            <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border-yellow-500/30 hover:border-yellow-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white uppercase tracking-tight text-sm">Leaderboard</p>
                    <p className="text-xs text-aura-text-muted">Compare athletes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Athletes List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Your Athletes
            </h2>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-aura-secondary hover:bg-aura-secondary/90 text-white text-sm" data-testid="add-athlete-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Athlete
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase tracking-tight">Add Athlete to Family</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddAthlete} className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Athlete's Email</Label>
                    <Input
                      type="email"
                      value={athleteEmail}
                      onChange={(e) => setAthleteEmail(e.target.value)}
                      placeholder="athlete@example.com"
                      className="bg-aura-bg border-white/10 text-white"
                      data-testid="athlete-email-input"
                    />
                    <p className="text-xs text-aura-text-subtle">
                      The athlete must have an existing Aura account
                    </p>
                  </div>
                  <Button
                    type="submit"
                    disabled={addingAthlete}
                    className="w-full bg-aura-secondary hover:bg-aura-secondary/90 text-white"
                    data-testid="confirm-add-athlete-btn"
                  >
                    {addingAthlete ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Link Athlete'
                    )}
                  </Button>
                </form>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-aura-text-muted text-center">
                    Family plan supports up to 4 athletes at $3 + $1 per additional athlete
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="grid gap-4">
              {[1, 2].map((i) => (
                <div key={i} className="aura-card p-6 animate-pulse">
                  <div className="h-20 bg-white/5 rounded-lg" />
                </div>
              ))}
            </div>
          ) : athletes.length > 0 ? (
            <div className="grid gap-4">
              {athletes.map((athlete) => (
                <Card key={athlete.user_id} className="bg-aura-surface border-white/10">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-aura-primary flex items-center justify-center flex-shrink-0">
                        {athlete.picture ? (
                          <img src={athlete.picture} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="font-heading font-bold text-white text-xl">
                            {athlete.name?.charAt(0)?.toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-heading font-bold text-lg text-white">{athlete.name}</h3>
                          <span className="text-xs text-aura-text-muted bg-white/5 px-2 py-0.5 rounded">
                            {athlete.sport || 'General'}
                          </span>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div>
                            <div className="flex items-center gap-1 text-aura-text-muted text-xs mb-1">
                              <Moon className="w-3 h-3" />
                              Recovery
                            </div>
                            <p className={`font-heading font-bold text-lg ${getRecoveryColor(athlete.latest_recovery?.recovery_score)}`}>
                              {athlete.latest_recovery?.recovery_score || '--'}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-1 text-aura-text-muted text-xs mb-1">
                              <Dumbbell className="w-3 h-3" />
                              Workouts
                            </div>
                            <p className="font-heading font-bold text-lg text-white">
                              {athlete.workouts_this_week || 0}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-1 text-aura-text-muted text-xs mb-1">
                              <TrendingUp className="w-3 h-3" />
                              Streak
                            </div>
                            <p className="font-heading font-bold text-lg text-aura-warning">
                              {athlete.streak || 0}d
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* View Button */}
                      <Link to={`/athlete/${athlete.user_id}`}>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-aura-text-muted hover:text-white"
                          data-testid={`view-athlete-${athlete.user_id}`}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-white mb-2">Start Your Family System</h3>
                <p className="text-aura-text-muted mb-4">
                  Add your athletes to begin supporting their development.
                </p>
                <Button className="bg-aura-secondary hover:bg-aura-secondary/90 text-white">
                  Add Your First Athlete
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Family Calendar Preview - This Week */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                This Week's Events
              </CardTitle>
              <Link to="/family/schedule" className="text-aura-secondary text-sm font-medium flex items-center gap-1">
                Manage <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {upcomingEvents.length > 0 ? (
              <div className="space-y-2">
                {upcomingEvents.slice(0, 5).map((event) => {
                  const athlete = getAthleteById(event.athlete_id);
                  const eventDate = parseISO(event.start_time);
                  return (
                    <div key={event.event_id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <div className={`w-10 h-10 rounded-lg ${getEventTypeColor(event.event_type)} bg-opacity-20 flex items-center justify-center`}>
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white truncate">{event.title}</p>
                          {event.requires_parent && (
                            <Badge className="bg-aura-secondary/20 text-aura-secondary border-0 text-xs">
                              <Users className="w-3 h-3" />
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-aura-text-muted">
                          {isToday(eventDate) ? 'Today' : isTomorrow(eventDate) ? 'Tomorrow' : format(eventDate, 'EEE, MMM d')} • {format(eventDate, 'h:mm a')}
                          {athlete ? ` • ${athlete.name}` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <Calendar className="w-10 h-10 text-aura-text-subtle mx-auto mb-2" />
                <p className="text-aura-text-muted">No events scheduled this week</p>
                <Link to="/family/schedule">
                  <Button className="mt-3 bg-aura-primary hover:bg-aura-primary-hover text-white">
                    Add Event
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Support Message */}
        <div className="aura-card p-6 text-center bg-gradient-to-br from-aura-secondary/5 to-transparent">
          <Users className="w-8 h-8 text-aura-secondary mx-auto mb-3" />
          <p className="font-heading font-bold text-xl text-white uppercase tracking-tight">
            "Aura is earned, not given."
          </p>
          <p className="text-aura-text-muted mt-2">
            Your athletes own their journey. You're here to encourage and support their development.
          </p>
        </div>
      </main>
    </div>
  );
};

export default SupporterDashboard;
